import moment from "moment";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { Avatar, Badge, EnvelopeIcon, fmtDate, resendWelcomeEmail, WhatsAppIcon, whatsappNumber } from "../../classes/members/memberUtils";
import { FcDrawer, Icon } from "../../classes/schedule/ui";
import { axisTick, bucketLabels, ChartShell, COLORS, fetchBuckets, findRange, LinkButton, Panel, PAST_RANGES, Picker, startDateFor, TooltipBox } from "./shared";

/**
 * "Members to follow up": GET bookings/memberInsights (one call). Lists are worked out here:
 * not booked 14/30/60+ days (activated members only), no/low credits (no subscription, ≤2 credits),
 * passes expiring soon, subscriptions ending, not activated yet.
 */

export const INSIGHT_KEYS = ["inactive", "credits", "expiring", "ending", "pending"];
const DOT = { inactive: "bg-[#D97706]", credits: "bg-portland-orange", expiring: "bg-indigo-bz", ending: "bg-light-brown-coffee", pending: "bg-[#2563EB]" };
const TONE = { inactive: "bg-[#FFF4E5] text-[#7A4A00]", credits: "bg-[#FFE8EB] text-[#B4232F]", expiring: "bg-[#F3E9FF] text-[#6A00BA]", ending: "bg-[#F1EAF0] text-brown-coffee", pending: "bg-[#E7F0FF] text-[#1D4ED8]" };
const ICONS = {
  inactive: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  credits: <path d="M3 9a2 2 0 0 0 0 6v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0 0-6V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" />,
  expiring: <><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  ending: <><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></>,
  pending: <><path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" /></>,
};
export const INACTIVE_DAYS = [14, 30, 60];

/** Loads memberInsights once; `lists` is null while loading. */
export function useMemberInsights() {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(false);
  const load = React.useCallback(async () => {
    setError(false);
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForBookings()}memberInsights` });
      if (res?.status === 200 && res?.data?.statusCode === 200 && Array.isArray(res.data.data?.members)) setData(res.data.data);
      else setError(true);
    } catch (e) {
      setError(true);
    }
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);

  const lists = React.useMemo(() => {
    if (!data) return null;
    const byId = new Map(data.members.map((m) => [m.consumerId, m]));
    const active = data.members.filter((m) => m.isVerified);
    const inactiveFor = (days) => {
      const cut = moment().subtract(days, "days");
      return active
        .filter((m) => (m.lastClassAt ? moment(m.lastClassAt).isBefore(cut) : moment(m.memberSince).isBefore(cut)))
        .sort((a, b) => (a.lastClassAt ? moment(a.lastClassAt).valueOf() : 0) - (b.lastClassAt ? moment(b.lastClassAt).valueOf() : 0));
    };
    const noSub = active.filter((m) => !m.hasSubscription);
    return {
      activeCount: active.length,
      byId,
      inactiveFor,
      none: noSub.filter((m) => m.credits <= 0),
      low: noSub.filter((m) => m.credits > 0 && m.credits <= 2),
      expiring: (data.expiring || []).map((x) => ({ ...x, member: byId.get(x.consumerId) })).filter((x) => x.member),
      ending: (data.ending || []).map((x) => ({ ...x, member: byId.get(x.consumerId) })).filter((x) => x.member),
      pending: data.members.filter((m) => !m.isVerified).sort((a, b) => moment(b.memberSince).valueOf() - moment(a.memberSince).valueOf()),
      expiringDays: data.expiringDays || 14,
    };
  }, [data]);

  const markResent = React.useCallback((consumerId) => {
    setData((d) => (d ? { ...d, members: d.members.map((m) => (m.consumerId === consumerId ? { ...m, welcomeEmailSentAt: new Date().toISOString() } : m)) } : d));
  }, []);

  return { lists, error, reload: load, markResent };
}

export const insightItems = (lists, inactiveDays = 30) =>
  lists
    ? [
        { key: "inactive", count: lists.inactiveFor(inactiveDays).length, label: `Not booked in ${inactiveDays}+ days` },
        { key: "credits", count: lists.none.length + lists.low.length, label: "No or low credits" },
        { key: "expiring", count: lists.expiring.length, label: `Passes expiring in ${lists.expiringDays} days` },
        { key: "ending", count: lists.ending.length, label: "Subscriptions ending" },
        { key: "pending", count: lists.pending.length, label: "Not activated yet" },
      ]
    : [];

/** Compact list for the Home overview. */
export function FollowUpList({ insights, onOpen, onSeeAll }) {
  const items = insightItems(insights.lists);
  return (
    <Panel title="Members to follow up" subtitle="Who could use a nudge" action={<LinkButton onClick={onSeeAll}>See all ›</LinkButton>}>
      <div className="mt-2">
        {insights.error ? (
          <p className="py-4 text-sm font-semibold text-gray-500">
            Couldn't load your members.{" "}
            <button type="button" onClick={insights.reload} className="font-extrabold text-indigo-bz">
              Try again
            </button>
          </p>
        ) : !insights.lists ? (
          INSIGHT_KEYS.map((k) => (
            <div key={k} className="flex animate-pulse items-center gap-3 border-t border-gray-100 py-3 first:border-t-0" aria-hidden="true">
              <span className="h-2.5 w-2.5 rounded-full bg-[#EDE5F0]" />
              <span className="h-3.5 flex-1 rounded-full bg-[#F3EEF5]" />
              <span className="h-4 w-6 rounded-full bg-[#EDE5F0]" />
            </div>
          ))
        ) : (
          items.map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={() => onOpen(it.key)}
              className="flex w-full items-center gap-3 border-t border-gray-100 py-3 text-left first:border-t-0 hover:bg-[#FBF8FE]"
            >
              <span className={classNames("h-2.5 w-2.5 shrink-0 rounded-full", DOT[it.key])} />
              <span className="flex-1 text-sm font-bold text-gray-700">{it.label}</span>
              <span className={classNames("text-base font-black", it.count ? "text-gray-900" : "text-gray-400")}>{it.count}</span>
              <Icon className="h-3.5 w-3.5 text-gray-300" d={<path d="m9 18 6-6-6-6" />} />
            </button>
          ))
        )}
      </div>
    </Panel>
  );
}

/** Tiles + drawer for the Members insights view. */
export function FollowUpSection({ insights, openKey, setOpenKey }) {
  const [inactiveDays, setInactiveDays] = React.useState(30);
  const [creditTab, setCreditTab] = React.useState("none");
  const items = insightItems(insights.lists, inactiveDays);
  return (
    <section>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-base font-black text-gray-900">Members to follow up</h2>
        <span className="text-[13px] text-gray-500">Open a list to see who, and reach out by WhatsApp or email.</span>
      </div>
      {insights.error ? (
        <div className="mt-3 rounded-[20px] border border-gray-200 bg-white px-6 py-8 text-center">
          <p className="text-sm font-bold text-gray-700">Couldn't load your members.</p>
          <button type="button" onClick={insights.reload} className="mt-2 rounded-xl px-4 py-2 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
            Try again
          </button>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {!insights.lists
            ? INSIGHT_KEYS.map((k) => (
                <div key={k} className="h-[136px] animate-pulse rounded-[20px] border border-gray-200 bg-white p-4" aria-hidden="true">
                  <span className="block h-8 w-8 rounded-xl bg-[#F3EEF5]" />
                  <span className="mt-3 block h-6 w-10 rounded-full bg-[#EDE5F0]" />
                  <span className="mt-3 block h-3 w-28 max-w-full rounded-full bg-[#F3EEF5]" />
                </div>
              ))
            : items.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setOpenKey(t.key)}
                  className="flex flex-col items-start gap-1.5 rounded-[20px] border border-gray-200 bg-white p-4 text-left shadow-[0_1px_2px_rgba(29,19,32,0.04)] transition hover:border-[#D9C2F2] hover:shadow-[0_8px_24px_rgba(126,0,221,0.08)]"
                >
                  <span className={classNames("flex h-8 w-8 items-center justify-center rounded-xl", TONE[t.key])}>
                    <Icon className="h-[18px] w-[18px]" d={ICONS[t.key]} />
                  </span>
                  <span className="mt-1 text-[26px] font-black leading-none text-gray-900">{t.count}</span>
                  <span className="text-sm font-extrabold leading-snug text-gray-900">{t.label}</span>
                  <span className="mt-auto pt-1 text-xs font-extrabold text-indigo-bz">See members ›</span>
                </button>
              ))}
        </div>
      )}
      {insights.lists && (
        <InsightDrawer
          openKey={openKey}
          onClose={() => setOpenKey(null)}
          lists={insights.lists}
          inactiveDays={inactiveDays}
          setInactiveDays={setInactiveDays}
          creditTab={creditTab}
          setCreditTab={setCreditTab}
          onResent={insights.markResent}
        />
      )}
    </section>
  );
}

const daysAgo = (d) => {
  const n = moment().startOf("day").diff(moment(d).startOf("day"), "days");
  return n <= 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`;
};
const passesLine = (m) => {
  if (m.unlimited) return "Unlimited subscription";
  if (m.hasSubscription) return "Subscription";
  if (!m.passes?.length) return "No pass or subscription";
  return m.passes.map((p) => `${p.name} · ${p.unused} ${p.unused === 1 ? "credit" : "credits"} left`).join(", ");
};

function InsightDrawer({ openKey, onClose, lists, inactiveDays, setInactiveDays, creditTab, setCreditTab, onResent }) {
  const navigate = useNavigate();
  const { addSuccessToast, addErrorToast } = useToast();
  const [sending, setSending] = React.useState(null);
  const [lastKey, setLastKey] = React.useState(openKey);
  React.useEffect(() => {
    if (openKey) setLastKey(openKey);
  }, [openKey]);
  const key = openKey || lastKey;

  let title = "";
  let subtitle = "";
  let foot = "";
  let tabs = null;
  let rows = [];
  if (key === "inactive") {
    title = "Not booked recently";
    subtitle = "Activated members with no class at your club for a while.";
    tabs = INACTIVE_DAYS.map((d) => ({ key: d, label: `${d}+ days`, count: lists.inactiveFor(d).length, on: inactiveDays === d, pick: () => setInactiveDays(d) }));
    rows = lists.inactiveFor(inactiveDays).map((m) => {
      const n = m.lastClassAt ? moment().startOf("day").diff(moment(m.lastClassAt).startOf("day"), "days") : null;
      return {
        m,
        detail: `${m.lastClassAt ? `Last class ${daysAgo(m.lastClassAt)}` : `Never booked · member since ${fmtDate(m.memberSince)}`} · ${passesLine(m)}`,
        badge: n === null ? { tone: "gray", text: "Never booked" } : { tone: n >= 60 ? "red" : "amber", text: `${n} days` },
      };
    });
    foot = "Based on the date of the last class they booked at your club. Removed and not-activated members are left out.";
  } else if (key === "credits") {
    title = "No or low credits";
    subtitle = "Members without a subscription who are out of credits, or nearly.";
    tabs = [
      { key: "none", label: "No credits", count: lists.none.length, on: creditTab === "none", pick: () => setCreditTab("none") },
      { key: "low", label: "1–2 credits left", count: lists.low.length, on: creditTab === "low", pick: () => setCreditTab("low") },
    ];
    rows = (creditTab === "none" ? lists.none : lists.low).map((m) => ({
      m,
      detail: m.credits > 0 ? passesLine(m) : `No pass with credits left${m.lastClassAt ? ` · last class ${daysAgo(m.lastClassAt)}` : ""}`,
      badge: m.credits > 0 ? { tone: "amber", text: `${m.credits} left` } : { tone: "red", text: "No credits" },
    }));
    foot = "Sell them a pass from their member page, with a payment link.";
  } else if (key === "expiring") {
    title = "Passes expiring soon";
    subtitle = `Passes that expire in the next ${lists.expiringDays} days with credits still unused.`;
    rows = lists.expiring.map((x) => ({ m: x.member, detail: `${x.name} · ${x.unused} ${x.unused === 1 ? "credit" : "credits"} unused`, badge: { tone: "amber", text: `Expires ${fmtDate(x.expiresAt, "D MMM")}` } }));
    foot = "Unused credits are lost when a pass expires.";
  } else if (key === "ending") {
    title = "Subscriptions ending";
    subtitle = "Cancelled subscriptions that stop at the end of their current period.";
    rows = lists.ending.map((x) => ({ m: x.member, detail: x.name, badge: { tone: "lilac", text: x.endsAt ? `Ends ${fmtDate(x.endsAt, "D MMM")}` : "Ending" } }));
    foot = "A good moment to ask why, or offer something that fits them better.";
  } else if (key === "pending") {
    title = "Not activated yet";
    subtitle = "Added to your club, but never signed in to the FitClub app.";
    rows = lists.pending.map((m) => ({
      m,
      detail: `Added ${fmtDate(m.memberSince)}${m.welcomeEmailSentAt ? ` · welcome email sent ${fmtDate(m.welcomeEmailSentAt)}` : ""}`,
      badge: { tone: "amber", text: "Not activated" },
      resend: true,
    }));
    foot = "They can book once they open the welcome email and set up their account.";
  }

  const resend = async (m) => {
    setSending(m.consumerId);
    const [ok, msg] = await resendWelcomeEmail(m.consumerId);
    setSending(null);
    if (ok) {
      addSuccessToast(msg);
      onResent(m.consumerId);
    } else addErrorToast(msg);
  };

  return (
    <FcDrawer open={!!openKey} onClose={onClose} eyebrow="MEMBERS TO FOLLOW UP" title={title} subtitle={subtitle} hideFooter>
      {tabs && (
        <div role="tablist" aria-label="Filter" className="flex w-fit max-w-full gap-1 overflow-x-auto rounded-2xl bg-[#F3EEF5] p-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={t.on}
              onClick={t.pick}
              className={classNames("h-9 shrink-0 whitespace-nowrap rounded-xl px-3.5 text-sm font-extrabold", t.on ? "bg-white text-brown-coffee shadow-sm" : "text-gray-500 hover:text-gray-900")}
            >
              {t.label} <span className="opacity-60">{t.count}</span>
            </button>
          ))}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#E3D0F7] bg-white px-6 py-10 text-center">
          <p className="text-base font-black text-gray-900">Nobody here</p>
          <p className="mt-1 text-sm text-gray-600">There's no one to follow up on in this list.</p>
        </div>
      ) : (
        <ul className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
          {rows.map(({ m, detail, badge, resend: canResend }, i) => {
            const wa = whatsappNumber(m);
            return (
              <li key={`${m.consumerId}-${i}`} className="flex flex-wrap items-center gap-3 border-t border-gray-100 px-4 py-3 first:border-t-0">
                <button type="button" onClick={() => navigate(`/mymembers/detail?id=${m.consumerId}&mid=${m.memberId}`)} className="flex min-w-0 flex-1 basis-full items-center gap-3 text-left sm:basis-0">
                  <Avatar src={m.profilePic} name={m.fullName} size={40} />
                  <span className="min-w-0">
                    <span className="block truncate text-[15px] font-extrabold text-gray-900 hover:text-indigo-bz">{m.fullName || m.email}</span>
                    <span className="mt-0.5 block text-[13px] leading-snug text-gray-600">{detail}</span>
                  </span>
                </button>
                {/* Phones: badge + actions on their own line under the name (pl = avatar 40 + gap 12) */}
                <div className="flex shrink-0 items-center gap-1.5 pl-[52px] sm:pl-0">
                  <Badge tone={badge.tone}>{badge.text}</Badge>
                  {canResend && (
                    <button
                      type="button"
                      disabled={sending === m.consumerId}
                      onClick={() => resend(m)}
                      className="flex h-9 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-[13px] font-extrabold text-gray-900 hover:bg-[#FBF8FE] disabled:opacity-60"
                    >
                      {sending === m.consumerId && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />}
                      Resend welcome email
                    </button>
                  )}
                  {!canResend && wa && (
                    <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${m.fullName}`} className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-[#1F6B3A] hover:bg-[#F2FBF5]">
                      <WhatsAppIcon className="size-4" />
                    </a>
                  )}
                  {!canResend && m.email && (
                    <a href={`mailto:${m.email}`} aria-label={`Email ${m.fullName}`} className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-[#FBF8FE]">
                      <EnvelopeIcon aria-hidden="true" className="size-4" />
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {foot && <p className="px-1 text-xs text-gray-500">{foot}</p>}
    </FcDrawer>
  );
}

/** New members per period (allMembersStats), with its own period picker. */
export function NewMembersCard() {
  const [rangeKey, setRangeKey] = React.useState("l12m");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const reqRef = React.useRef(0);
  const range = findRange(PAST_RANGES, rangeKey);

  const load = React.useCallback(async () => {
    const id = ++reqRef.current;
    setLoading(true);
    setError("");
    try {
      const buckets = await fetchBuckets("allMembersStats", { interval: range.interval, size: range.size, startDate: startDateFor(range, false) });
      if (id !== reqRef.current) return;
      setRows(buckets.map((b) => ({ ...bucketLabels(b, range.interval, range.size), value: parseFloat(b.count) || 0 })));
    } catch (e) {
      if (id === reqRef.current) setError(e.message || "Couldn't load these stats.");
    } finally {
      if (id === reqRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);
  React.useEffect(() => {
    load();
  }, [load]);

  const sum = rows.reduce((a, r) => a + r.value, 0);
  return (
    <Panel
      title="New members"
      subtitle={
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl font-black text-gray-900">{loading ? "–" : sum}</span>
          <span className="font-bold text-gray-600">{loading ? "" : `joined in the ${range.title.toLowerCase()}`}</span>
        </span>
      }
      action={<Picker label="New members period" value={rangeKey} groups={PAST_RANGES} onChange={setRangeKey} />}
    >
      <ChartShell loading={loading} error={error} onRetry={load} empty={!loading && sum === 0} emptyText={`No new members in the ${range.title.toLowerCase()}`} height={240}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 4, left: -16, bottom: 0 }} barCategoryGap={range.size > 15 ? "18%" : "28%"}>
            <CartesianGrid vertical={false} stroke="#F1EEF2" />
            <XAxis dataKey="short" tick={axisTick} tickLine={false} axisLine={{ stroke: "#E3DCE6" }} interval="preserveStartEnd" minTickGap={8} />
            <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} width={44} />
            <Tooltip cursor={{ fill: "#F7F4F8" }} content={<TooltipBox rows={(d) => [{ label: "New members", value: d.value, color: COLORS.members }]} />} />
            <Bar dataKey="value" fill={COLORS.members} radius={[4, 4, 0, 0]} maxBarSize={44} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>
    </Panel>
  );
}
